import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'

import { UNREAD_NOTIFICATIONS_COUNT } from '@/graphql/queries/Notifications'
import { useRouter, useCompanySetting, useLoggedInUser } from '@/hooks'
import menuItems from '@/const/menuItems'

// if there are multiple components using useTitle, we want to prioritize the one with a custom title
let hookWithCustomTitleIsInUse = false

export default function useTitle(customTitle?: string) {
  const [windowTitle] = useCompanySetting('WINDOW_TITLE')
  const { companyId } = useLoggedInUser(true)
  const { data } = useQuery(UNREAD_NOTIFICATIONS_COUNT, {
    skip: !companyId,
  })
  const { location } = useRouter()
  const { t } = useTranslation()

  useEffect(() => {
    if (hookWithCustomTitleIsInUse && !customTitle) {
      return
    }

    hookWithCustomTitleIsInUse = !!customTitle

    const baseTitle = windowTitle || 'HR-ON Staff'

    const unreadCount =
      (data?.notificationEmployees?.totalCount ?? 0) > 0
        ? `(${data?.notificationEmployees?.totalCount})`
        : ''

    const paths = location.pathname.split('/')

    // root paths always have appended AND prepended slashes for whatever reason
    const rootPath = `/${paths[1]}/`

    // subpaths dont use slashes
    const subPath = paths[2]

    // no need to add proper checks to nativeAppAccess and canViewDashboardPage, since we just use menuItems as a lookup
    const route = !customTitle
      ? menuItems({
          nativeAppAccess: true,
          canViewDashboardPage: true,
          companyId,
          t,
        }).find(item => item.path === rootPath)
      : undefined

    const pathName =
      customTitle ||
      (subPath &&
        route?.subMenuItems?.find(
          item => item.to?.replace('/*', '') === subPath,
        )?.label) ||
      route?.title ||
      ''

    const pathNameWithDivider = pathName ? ` ${pathName} |` : ''

    document.title = `${unreadCount}${pathNameWithDivider} ${baseTitle}`

    return () => {
      hookWithCustomTitleIsInUse = false
    }
  }, [
    location.pathname,
    customTitle,
    windowTitle,
    data?.notificationEmployees?.totalCount,
  ])
}
